import { createStore } from "jotai";

import { Provider } from "jotai/react";

import { memo, useEffect, useState } from "react";

import aiBotService from "./services/bot/aiBot";

import { Toaster } from "sileo";

import { openDatabase } from "./Utils/db";

import "./internalization";

import Routes from "./Routes";

import { useEventBusBridge } from "./services/eventBus/eventBusBridge";

import { useAuthBootstrap } from "./hooks/useAuthBootstrap";

import { syncSessionWithExtension } from "./lib/syncSessionWithExtension";

import { SceneSyncPublisher } from "./broadcast/SceneSyncPublisher";

import ErrorBoundary from "./components/ErrorBoundary";

import { DesktopUpdatePrompt } from "./components/DesktopUpdatePrompt/DesktopUpdatePrompt";
import {
  adoptDesktopPorts,
  DEFAULT_LOCAL_API_PORT,
  storedLocalApiPort,
} from "./lib/localApi";

const myStore = createStore();

// Resuelve el puerto de la API local (Electron) y lo persiste ANTES de que cualquier repo arme su

// base URL. Sin esto, el primer fetch (p.ej. /api/workspaces) puede correr antes de que el puerto

// esté seteado → `http://localhost:null/...` → falla → pantalla en blanco en la vista autenticada.

async function ensureApiPort(): Promise<void> {
  const api = window?.electronAPI;

  if (!api?.getPorts) return; // browser / sin Electron: no aplica

  try {
    const result = await api.getPorts();

    if (adoptDesktopPorts(result) === null) {
      console.warn(
        `[ports] getPorts no trajo un puerto válido; los repos usan ${DEFAULT_LOCAL_API_PORT}`,
        result,
      );
    }
  } catch (err) {
    console.error("❌ Error getting ports:", err);
  }
}

const syncDesktopContentProtectionWithExtension = async () => {
  const electronAPI = window?.electronAPI;

  if (!electronAPI?.setContentProtection) return;

  try {
    const db = await openDatabase();

    const userConfig = await db?.config

      .where("userId")

      .equals("addressBlockChain")

      .first();

    const enabled =
      userConfig?.configuration?.experiments?.desktopContentProtection ?? false;

    await electronAPI.setContentProtection(enabled);
  } catch (err) {
    console.warn("Could not sync content protection with extension:", err);
  }
};

// Mounts the SSE→atoms/Dexie/TriggerEngine bridge once for the app lifetime.

// Lives inside <Provider> so its `useAtomValue`/`useSetAtom` calls see the

// same Jotai store as the rest of the tree.

const EventBusBridge = (): null => {
  useEventBusBridge();

  return null;
};

// Bootstraps cookie-based auth state on mount. Lives inside <Provider> so

// useSetAtom finds the right Jotai store. See atoms/auth.atoms.ts for why.

const AuthBootstrap = (): null => {
  useAuthBootstrap();

  return null;
};

const Root = () => {
  const [portReady, setPortReady] = useState(false);

  useEffect(() => {
    // Gatea el render hasta resolver el puerto de la API local (evita fetch a localhost:null →

    // pantalla en blanco). Timeout de 3s para no colgar la app si getPorts nunca resuelve.

    let cancelled = false;

    const t = setTimeout(() => {
      if (cancelled) return;

      if (!storedLocalApiPort()) {
        console.warn(
          `[ports] getPorts no respondió en 3 s; los repos usan ${DEFAULT_LOCAL_API_PORT}`,
        );
      }

      setPortReady(true);
    }, 3000);

    void ensureApiPort().finally(() => {
      if (!cancelled) {
        clearTimeout(t);

        setPortReady(true);
      }
    });

    return () => {
      cancelled = true;

      clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    syncSessionWithExtension();

    syncDesktopContentProtectionWithExtension();
  }, []);

  // Eriol Bot AI layer (semantic moderation + !ask). App-lifetime like

  // the EventBus bridge; it no-ops internally while both features are

  // disabled in its config, so starting it unconditionally is cheap.

  useEffect(() => {
    if (!portReady) return;

    aiBotService.start();
  }, [portReady]);

  if (!portReady) return null;

  return (
    <Provider store={myStore}>
      <AuthBootstrap />
      <EventBusBridge />
      <SceneSyncPublisher />
      <DesktopUpdatePrompt />
      <Toaster
        position="bottom-right"
        options={{ fill: "#1e1e2e", styles: { description: "color: #e0e0e0" } }}
      />
      {/* App-wide top-level boundary: catches uncaught render errors anywhere
          below (and reports them to Sentry via componentDidCatch). */}
      <ErrorBoundary>
        <Routes />
      </ErrorBoundary>
    </Provider>
  );
};

export default memo(Root);
